import { useState, useCallback, type FormEvent } from 'react';
import { ApiClientError } from '../services/api-client';
import { createDividend } from '../services/dividend-service';
import { validateDividendInput, type ValidationError } from '@covered-calls/shared';
import type { DividendInput } from '@covered-calls/shared';

/**
 * AddDividendDialog — record a cash dividend received on the underlying stock
 * of an existing position. Posts to POST /api/trades/{id}/dividends via
 * createDividend.
 *
 * The dividend is a CREDIT (income received). The stored amount is derived as
 * perShare × position quantity; a live preview mirrors that computation as the
 * per-share rate changes. Unlike the protective-put dialog, dividends carry no
 * commission and no broker-profile handling.
 *
 * Validates: Requirements 1.1, 1.2, 2.1, 2.2, 2.3, 2.4
 */

export interface AddDividendDialogProps {
  positionId: string;
  ticker: string;
  quantity: number;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormState {
  perShare: string;
  paymentDate: string;
}

const initialFormState: FormState = {
  perShare: '',
  paymentDate: '',
};

export function AddDividendDialog({
  positionId,
  ticker,
  quantity,
  onSuccess,
  onCancel,
}: AddDividendDialogProps) {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Live preview of the derived dividend amount = perShare × quantity. Mirrors
  // the backend derivation; NaN/blank input previews as 0.00.
  const previewAmount = (parseFloat(form.perShare) || 0) * quantity;

  const handleFieldChange = useCallback(
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => prev.filter((err) => err.field !== field));
      setServerError(null);
    },
    []
  );

  const getFieldError = (field: string): string | undefined => {
    return errors.find((err) => err.field === field)?.message;
  };

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setServerError(null);

      const input: DividendInput = {
        perShare: parseFloat(form.perShare),
        paymentDate: form.paymentDate,
      };

      const validationErrors = validateDividendInput(input);
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      }

      setErrors([]);
      setIsSubmitting(true);

      try {
        await createDividend(positionId, input);
        onSuccess();
      } catch (error) {
        if (error instanceof ApiClientError) {
          if (error.status === 409) {
            setServerError(
              'This dividend could not be saved because the position changed elsewhere. Please reload and try again.'
            );
          } else if (error.errors && error.errors.length > 0) {
            setErrors(error.errors.map((err) => ({ field: err.field, message: err.message })));
          } else {
            setServerError(error.message || 'An error occurred. Please try again.');
          }
        } else {
          setServerError('An unexpected error occurred. Please try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [form, positionId, onSuccess]
  );

  return (
    <div
      role="dialog"
      aria-labelledby="add-dividend-dialog-title"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '8px',
          padding: '2rem',
          maxWidth: '560px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
        }}
      >
        <h2 id="add-dividend-dialog-title" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
          Add Dividend — {ticker}
        </h2>
        <p style={{ color: 'var(--color-muted)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
          Record a cash dividend received on your {quantity.toLocaleString()} shares of {ticker}.
          The income is a credit added to the position P&amp;L.
        </p>

        {serverError && (
          <div
            role="alert"
            style={{
              backgroundColor: 'var(--color-chip-danger-bg)',
              color: 'var(--color-chip-danger-text)',
              padding: '0.75rem 1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              fontSize: '0.875rem',
            }}
          >
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Per Share */}
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="add-dividend-per-share"
              style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
            >
              Per Share ($ per share)
            </label>
            <input
              id="add-dividend-per-share"
              type="number"
              step="0.01"
              min="0.01"
              max="999999.99"
              value={form.perShare}
              onChange={handleFieldChange('perShare')}
              aria-invalid={!!getFieldError('perShare')}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('perShare') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('perShare') && (
              <span style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>{getFieldError('perShare')}</span>
            )}
          </div>

          {/* Payment Date */}
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="add-dividend-payment-date"
              style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}
            >
              Payment Date
            </label>
            <input
              id="add-dividend-payment-date"
              type="date"
              value={form.paymentDate}
              onChange={handleFieldChange('paymentDate')}
              aria-invalid={!!getFieldError('paymentDate')}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: `1px solid ${getFieldError('paymentDate') ? 'var(--color-danger)' : 'var(--color-input-border)'}`,
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
            />
            {getFieldError('paymentDate') && (
              <span style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>{getFieldError('paymentDate')}</span>
            )}
          </div>

          {/* Live derived amount preview */}
          <div
            data-testid="add-dividend-amount-preview"
            style={{
              marginBottom: '1.5rem',
              padding: '0.75rem 1rem',
              backgroundColor: 'var(--color-surface-2)',
              borderRadius: '4px',
              fontSize: '0.9375rem',
              color: 'var(--color-text)',
              fontWeight: 500,
            }}
          >
            Dividend amount: ${previewAmount.toFixed(2)}
            <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-muted)', fontWeight: 400 }}>
              Per share × {quantity.toLocaleString()} shares.
            </span>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-muted)',
                color: 'var(--color-on-brand)',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                minHeight: '44px',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--color-success)',
                color: 'var(--color-on-brand)',
                border: 'none',
                borderRadius: '4px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                minHeight: '44px',
                opacity: isSubmitting ? 0.7 : 1,
              }}
            >
              {isSubmitting ? 'Adding...' : 'Add Dividend'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
